import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import ParentLeadsUpgradeModal from '../components/ParentLeadsUpgradeModal';
import { useAuth } from './AuthContext';

/**
 * Global upgrade modal context.
 * Any component can open the modal via `useUpgrade().open()`.
 * Additionally, an axios response interceptor catches HTTP 402 with
 * `code === 'TIER_LOCKED'` and auto-opens the modal — so any locked
 * SIS action (Présences, Frais, Notes, …) instantly triggers conversion.
 *
 * IMPORTANT — Unverified candidates only ever see the 25€ activation
 * paywall. Any attempt to show the 290€ Partner modal for a candidate is
 * silently rerouted to the Activation modal via a CustomEvent.
 */
const UpgradeContext = createContext({ open: () => {}, close: () => {} });

export const useUpgrade = () => useContext(UpgradeContext);

export const UpgradeProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [defaultCycle, setDefaultCycle] = useState('yearly');
  const { user } = useAuth() || {};
  const isCandidate = (user?.status || '').toLowerCase() === 'unverified_candidate';

  const redirectToActivation = () => {
    try {
      window.dispatchEvent(new CustomEvent('open-activation-modal', {
        detail: { reason: 'Cette fonctionnalité nécessite d\'abord l\'activation 25 € / 16 000 FCFA.' },
      }));
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const data = error?.response?.data;
        const status = error?.response?.status;
        const code = typeof data?.detail === 'object' ? data.detail?.code : data?.code;
        if (status === 402 && (code === 'TIER_LOCKED' || code === 'QUOTA_EXCEEDED' || code === 'ANNUAL_REQUIRED')) {
          // Candidates never see the 290€ modal — re-route to activation.
          if (isCandidate) {
            redirectToActivation();
          } else {
            if (code === 'QUOTA_EXCEEDED' || code === 'ANNUAL_REQUIRED') setDefaultCycle('yearly');
            setOpen(true);
          }
        }
        return Promise.reject(error);
      }
    );

    const handler = (e) => {
      if (isCandidate) {
        redirectToActivation();
        return;
      }
      if (e?.detail?.defaultCycle) setDefaultCycle(e.detail.defaultCycle);
      setOpen(true);
    };
    window.addEventListener('open-upgrade-modal', handler);

    return () => {
      axios.interceptors.response.eject(interceptorId);
      window.removeEventListener('open-upgrade-modal', handler);
    };
  }, [isCandidate]);

  const value = {
    open: (opts = {}) => {
      if (isCandidate) {
        redirectToActivation();
        return;
      }
      if (opts.defaultCycle) setDefaultCycle(opts.defaultCycle);
      setOpen(true);
    },
    close: () => setOpen(false),
  };

  return (
    <UpgradeContext.Provider value={value}>
      {children}
      {/* For candidates the 290€ modal is permanently muted. */}
      {!isCandidate && (
        <ParentLeadsUpgradeModal open={open} onClose={() => setOpen(false)} defaultCycle={defaultCycle} />
      )}
    </UpgradeContext.Provider>
  );
};

export default UpgradeProvider;
